import debounce from 'lodash/debounce';

interface IConfig {
  scrollThreshold: number;
}

interface IHandler {
  id: string;
  handler: () => void;
  context?: ('onMobile' | 'onDesktop')[];
}

// this utility is from 'use-exit-intent' repo.
// moved it over to have a matching continuity between library and this custom hook
function isTouchInput() {
  if (typeof window === 'undefined') return;

  return (
    window.matchMedia('(hover: none)').matches ||
    navigator.userAgent.toLowerCase().includes('mobile')
  );
}

export default function useScrollExitIntent(config: IConfig) {
  const scrollThreshold = config.scrollThreshold / 100;

  const scrollDetector = ({ onDetect }: { onDetect: () => void }) => {
    let scrollThresholdCrossed = false;
    let lastScrollPosition = 0;
    let lastScrollTime = 0;

    const handleScroll = () => {
      const currentScrollPosition = window.scrollY;
      const documentHeight = document.documentElement.scrollHeight;
      const windowHeight = window.innerHeight;
      const scrollThresholdPx =
        documentHeight * scrollThreshold - windowHeight * scrollThreshold;

      // Detect scroll down
      if (
        !scrollThresholdCrossed &&
        currentScrollPosition > scrollThresholdPx
      ) {
        scrollThresholdCrossed = true;
      }

      // Detect quick scroll up
      const currentTime = Date.now();
      const timeDiff = currentTime - lastScrollTime;
      const scrollDiff = lastScrollPosition - currentScrollPosition;
      const isQuickScrollUp = scrollDiff > 50 && timeDiff < 200;

      if (isQuickScrollUp && scrollThresholdCrossed) {
        onDetect();
      }

      lastScrollPosition = currentScrollPosition;
      lastScrollTime = currentTime;
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  };

  const registerHandler = ({ handler, context }: IHandler) => {
    let debouncedHandler = () => {};

    if (context) {
      if (context.includes('onMobile') && isTouchInput()) {
        debouncedHandler = debounce(handler, 300);
      }
      if (context.includes('onDesktop') && !isTouchInput()) {
        debouncedHandler = debounce(handler, 300);
      }
    } else {
      // no context provided, default behavior
      debouncedHandler = debounce(handler, 300);
    }

    const cleanup = scrollDetector({ onDetect: debouncedHandler });

    return { cleanup };
  };

  return { registerHandler };
}
